/**
 * WellnessPendingBadge
 * -----------------------------------------------------------------------------
 * A subtle, non-technical indicator that summarises pending wellness updates
 * across the small set of local-first flows we own:
 *   - Daily Check-Ins
 *   - Wellness Reminders
 *   - Step Tracker (manual entries logged while offline)
 *
 * Design rules (deliberate):
 *   - Hide entirely when nothing is queued AND we're online. The happy path
 *     must be silent — no green "Synced" pill, no spinner — to keep the
 *     Profile screen calm.
 *   - Use friendly wellness language. No "queue", "backend", "error".
 *   - Aggregate counts across all wellness queues so the user sees ONE
 *     truthful number instead of three competing pills.
 *   - The `panel` variant is expandable: collapsed by default, taps reveal
 *     a calm one-line-per-entry list ("Daily check-in saved offline · 2 min
 *     ago"). No technical detail, no payload contents, no IDs.
 *
 * Scope (strict allow-list — DO NOT add prescriptions, clinical records,
 * payments, or specialist data here):
 *   - checkinQueue   (from wellnessQueues.js)
 *   - remindersQueue (from wellnessQueues.js)
 *   - step tracker pending (read via deviceHealthService.getPending())
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Cloud, CloudOff, RefreshCw, ChevronDown, ChevronUp, CheckCircle2, Bell, Droplet, Footprints, Smile } from 'lucide-react';
import { checkinQueue, remindersQueue } from '@/services/wellnessQueues';
import { QUEUE_STATUS, isOnline } from '@/services/localFirstQueue';
import deviceHealthService from '@/services/deviceHealthService';

// ---------------------------------------------------------------------------
// Helpers — kept module-scoped so they're stable across renders and easy to
// unit-test if we ever add Jest coverage for the badge.
// ---------------------------------------------------------------------------

function readStepPending() {
  try { return deviceHealthService.getPending() || []; } catch (_) { return []; }
}

/**
 * Compact relative-time formatter — duplicated from StepTrackerEnhanced for
 * isolation. Returns short human strings: "just now / 2 min ago / 1 hr ago".
 */
function formatRelative(iso) {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 30 * 1000) return 'just now';
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  const days = Math.floor(hrs / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

/**
 * Map a raw queued payload to a friendly user-facing line. The transform is
 * deliberately one-way and lossy — we never expose IDs, payload contents,
 * or technical operation names. Returns:
 *   { label, sub, Icon }
 */
function describeCheckinEntry(entry) {
  const sm = entry?.payload?.smart || {};
  const tasks = entry?.payload?.taskIds || [];

  // If the only meaningful signal is hydration, give it a hydration label so
  // a user who tapped "Yes, drank water" feels their action was recognised.
  const onlyHydration = tasks.length === 1 && tasks[0] === 'water';
  if (onlyHydration) {
    return { label: 'Hydration update waiting', sub: formatRelative(entry?.queuedAt), Icon: Droplet };
  }

  // Pure mood / feelings entry — no tracker tasks but mood signal present.
  const moodOnly = tasks.length === 0 && (sm.mood || (Array.isArray(sm.feelings) && sm.feelings.length > 0));
  if (moodOnly) {
    return { label: 'Mood entry saved offline', sub: formatRelative(entry?.queuedAt), Icon: Smile };
  }

  return { label: 'Daily check-in saved offline', sub: formatRelative(entry?.queuedAt), Icon: CheckCircle2 };
}

function describeReminderEntry(entry) {
  const op = entry?.payload?.op;
  let label = 'Reminder update waiting';
  if (op === 'create') label = 'New reminder waiting';
  else if (op === 'update') label = 'Reminder change waiting';
  else if (op === 'delete') label = 'Removed reminder waiting';
  else if (op === 'complete') label = 'Completed reminder waiting';
  return { label, sub: formatRelative(entry?.queuedAt), Icon: Bell };
}

function describeStepEntry(entry) {
  return { label: 'Step update saved offline', sub: formatRelative(entry?.queuedAt), Icon: Footprints };
}

/**
 * Gather raw queued entries and project them into friendly list items. We
 * preserve insertion order within each source so the user sees their actions
 * in roughly the order they performed them.
 */
function readPendingEntries() {
  const out = [];
  try {
    for (const e of checkinQueue.peek()) {
      const d = describeCheckinEntry(e);
      out.push({ key: `c-${e.clientId}`, source: 'checkin', queuedAt: e.queuedAt, ...d });
    }
  } catch (_) {}
  try {
    for (const e of remindersQueue.peek()) {
      const d = describeReminderEntry(e);
      out.push({ key: `r-${e.clientId}`, source: 'reminders', queuedAt: e.queuedAt, ...d });
    }
  } catch (_) {}
  try {
    for (const e of readStepPending()) {
      const d = describeStepEntry(e);
      out.push({ key: `s-${e.queuedAt || e.date}`, source: 'steps', queuedAt: e.queuedAt, ...d });
    }
  } catch (_) {}
  // Newest first feels more reassuring ("yes, your most recent action is here").
  return out.sort((a, b) => new Date(b.queuedAt || 0) - new Date(a.queuedAt || 0));
}

function computeAggregate() {
  return {
    online: isOnline(),
    checkin: checkinQueue.size(),
    reminders: remindersQueue.size(),
    steps: readStepPending().length,
    checkinStatus: checkinQueue.getStatus(),
    remindersStatus: remindersQueue.getStatus(),
  };
}

/**
 * Resolve the user-facing copy for the summary line. Kept separate from the
 * list so the wording is easy to tweak and impossible to drift between
 * variants. Returns null when there's nothing meaningful to show.
 */
function resolveMessage({ online, total, anyFlushing }) {
  if (total === 0) return null;

  if (!online) {
    return {
      text: total === 1
        ? '1 update waiting to sync'
        : `${total} wellness updates pending`,
      sub: 'They\'ll sync automatically when you\'re back online.',
      tone: 'calm',
      Icon: CloudOff,
    };
  }
  if (anyFlushing) {
    return {
      text: 'Syncing wellness entries…',
      tone: 'syncing',
      Icon: RefreshCw,
      spin: true,
    };
  }
  return {
    text: total === 1
      ? '1 wellness update pending'
      : `${total} wellness updates pending`,
    sub: 'We\'ll finish syncing in the background.',
    tone: 'pending',
    Icon: Cloud,
  };
}

const TONE_CLASSES = {
  calm:    'border-slate-200 bg-slate-50 text-slate-700',
  pending: 'border-emerald-200 bg-emerald-50 text-emerald-800',
  syncing: 'border-teal-200 bg-teal-50 text-teal-800',
};

/**
 * variant:
 *   - 'chip'   — compact pill. Inline placement (e.g. Dashboard header).
 *                Never expandable — chip is a quick glance only.
 *   - 'panel'  — expandable card. Default collapsed; tap to reveal a calm,
 *                one-line-per-entry list of what's still waiting. Good for
 *                the Profile screen where the user has time to scan it.
 */
export function WellnessPendingBadge({ variant = 'chip', className = '', 'data-testid': testId }) {
  const [agg, setAgg] = useState(() => computeAggregate());
  const [entries, setEntries] = useState(() => readPendingEntries());
  const [expanded, setExpanded] = useState(false);
  const [, setTick] = useState(0);

  const refresh = useCallback(() => {
    setAgg(computeAggregate());
    setEntries(readPendingEntries());
  }, []);

  useEffect(() => {
    const unsubCheckin = checkinQueue.subscribe(refresh);
    const unsubReminders = remindersQueue.subscribe(refresh);
    const onFocus = () => refresh();
    const onStorage = (e) => {
      if (!e || !e.key) return;
      if (e.key.startsWith('healthloop.queue.') || e.key === 'healthloop.steps.pendingQueue') {
        refresh();
      }
    };
    const onOnline = () => refresh();
    window.addEventListener('focus', onFocus);
    window.addEventListener('storage', onStorage);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOnline);
    const interval = setInterval(refresh, 15000);
    return () => {
      unsubCheckin();
      unsubReminders();
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOnline);
      clearInterval(interval);
    };
  }, [refresh]);

  // 30-second tick — keeps relative-time labels fresh ("just now" → "1 min ago").
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 30000);
    return () => clearInterval(id);
  }, []);

  const total = agg.checkin + agg.reminders + agg.steps;
  const anyFlushing =
    agg.checkinStatus === QUEUE_STATUS.FLUSHING ||
    agg.remindersStatus === QUEUE_STATUS.FLUSHING;

  // Auto-collapse the moment the list goes empty — saves a re-render flash
  // and matches the "auto-clear after reconnect" expectation.
  useEffect(() => {
    if (total === 0 && expanded) setExpanded(false);
  }, [total, expanded]);

  const message = resolveMessage({ online: agg.online, total, anyFlushing });
  if (!message) return null;

  const { text, sub, tone, Icon, spin } = message;
  const toneClass = TONE_CLASSES[tone] || TONE_CLASSES.calm;

  // ---- chip (compact, never expandable) ---------------------------------
  if (variant === 'chip') {
    return (
      <span
        className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium ${toneClass} ${className}`}
        data-testid={testId || 'wellness-pending-chip'}
        role="status"
        aria-live="polite"
        title={sub || text}
      >
        <Icon className={`h-3.5 w-3.5 ${spin ? 'animate-spin' : ''}`} aria-hidden="true" />
        <span data-testid="wellness-pending-text">{text}</span>
      </span>
    );
  }

  // ---- panel (collapsible) ----------------------------------------------
  const canExpand = entries.length > 0;
  const showList = expanded && canExpand;

  return (
    <div
      className={`rounded-xl border ${toneClass} ${className}`}
      data-testid={testId || 'wellness-pending-panel'}
      role="status"
      aria-live="polite"
    >
      <button
        type="button"
        onClick={() => canExpand && setExpanded((v) => !v)}
        disabled={!canExpand}
        aria-expanded={showList}
        aria-controls="wellness-pending-list"
        className={`w-full flex items-start gap-3 px-4 py-3 text-left ${canExpand ? 'cursor-pointer hover:bg-black/5' : 'cursor-default'} rounded-xl transition-colors`}
        data-testid="wellness-pending-toggle"
      >
        <Icon className={`h-4.5 w-4.5 shrink-0 mt-0.5 ${spin ? 'animate-spin' : ''}`} aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium" data-testid="wellness-pending-text">{text}</p>
          {sub && <p className="text-xs opacity-80 mt-0.5">{sub}</p>}
        </div>
        {canExpand && (
          showList
            ? <ChevronUp className="h-4 w-4 shrink-0 mt-1 opacity-70" aria-hidden="true" />
            : <ChevronDown className="h-4 w-4 shrink-0 mt-1 opacity-70" aria-hidden="true" />
        )}
      </button>

      {showList && (
        <ul
          id="wellness-pending-list"
          className="border-t border-current/10 px-2 py-1.5 space-y-0.5"
          data-testid="wellness-pending-list"
        >
          {entries.map((e) => {
            const EntryIcon = e.Icon || Cloud;
            return (
              <li
                key={e.key}
                className="flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-black/5 transition-colors"
                data-testid={`wellness-pending-entry-${e.source}`}
              >
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-white/70 text-slate-600">
                  <EntryIcon className="h-3.5 w-3.5" aria-hidden="true" />
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-slate-800 truncate">{e.label}</p>
                  {e.sub && (
                    <p className="text-[11px] text-slate-500 mt-0.5">{e.sub}</p>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default WellnessPendingBadge;
